'use client';

import { memo, useCallback, useMemo } from 'react';

import { HeaderModals } from '@/components/header/HeaderModals';
import { HeaderNotificationButton } from '@/components/header/HeaderNotificationButton';
import { HeaderWalletButton } from '@/components/header/HeaderWalletButton';
import { LanguageSwitcher } from '@/components/header/LanguageSwitcher';
import { Navigation } from '@/components/header/Navigation';
import { WalletDisplay } from '@/components/header/WalletDisplay';
import { useWalletConnection } from '@/hooks/useWalletConnection';
import { useRouter } from '@/i18n/navigation';
import { useSignOut } from '@/lib/auth/useSignOut.client';
import { cn } from '@/lib/utils/cn';
import { C_Routes } from '@/ts/constants/C_Routes';
import { E_Routes } from '@/ts/enums/E_Routes';

interface I_HeaderClientProps {
  firstPoolSlug?: string | null;
  type?: 'default' | 'reward';
  isNotFound?: boolean;
}

export const HeaderClient = memo(function HeaderClient({
  firstPoolSlug,
  type,
  isNotFound = false
}: I_HeaderClientProps) {
  const isReward = type === 'reward';
  const {
    // State
    isFullyLoggedIn,
    isLoading,
    isConnected,
    disconnectAsync,

    // Actions
    openWalletConnect,
    closeWalletConnect,
    closeWelcome,
    openAccount,
    closeAccount,
    handleWalletPick,
    setPendingWallet
  } = useWalletConnection();

  const signOut = useSignOut();
  const router = useRouter();

  const handleWalletIconClick = useCallback(async () => {
    if (isReward) return;
    if (isLoading) {
      return;
    }

    if (isFullyLoggedIn) {
      openAccount();
    } else {
      openWalletConnect();
    }
  }, [
    isLoading,
    isFullyLoggedIn,
    isConnected,
    openAccount,
    disconnectAsync,
    openWalletConnect
  ]);

  const handleEditProfile = useCallback(() => {
    closeWelcome();
    // Navigation is now handled by the Link component in WelcomeDialog
  }, [closeWelcome]);

  // Handle sign out
  const handleSignOut = useCallback(async () => {
    try {
      closeAccount();
      await signOut();
    } catch (error) {
      console.error('Sign out failed:', error);
    }
    router.push(C_Routes[E_Routes.HOME].link);
  }, [closeAccount, signOut, router]);

  // Memoize modal props object to prevent unnecessary re-renders
  const modalProps = useMemo(
    () => ({
      closeWalletConnect,
      closeWelcome,
      closeAccount,
      handleWalletPick,
      setPendingWallet,
      handleEditProfile,
      handleSignOut
    }),
    [
      closeWalletConnect,
      closeWelcome,
      closeAccount,
      handleWalletPick,
      setPendingWallet,
      handleEditProfile,
      handleSignOut
    ]
  );

  return (
    <>
      {!isReward && (
        <div className="hidden md:flex items-center justify-center order-2 w-full">
          <Navigation
            className="md:flex hidden"
            firstPoolSlug={firstPoolSlug}
            isNotFound={isNotFound}
          />
        </div>
      )}
      <div
        className={cn(
          'flex items-center justify-end gap-2 md:gap-4 order-3 md:order-4',
          isReward && !isFullyLoggedIn ? 'md:w-0' : ''
        )}
      >
        <div className="flex items-end justify-start">
          <LanguageSwitcher />
        </div>
        <HeaderNotificationButton type={type} />
        <HeaderWalletButton onWalletClick={handleWalletIconClick} type={type} />
        <div
          className={cn(
            'hidden md:block  md:min-w-33',
            isReward && !isFullyLoggedIn ? 'md:min-w-0' : ''
          )}
        >
          <WalletDisplay onWalletClick={handleWalletIconClick} type={type} />
        </div>
      </div>

      <HeaderModals {...modalProps} />
    </>
  );
});

HeaderClient.displayName = 'HeaderClient';
